import {useSelector} from "react-redux";
import Project from "./project";
import AvatarGroup from "../../system/components/avatar-group";
import Avatar from "../../system/components/avatar";
import {normalizeMembershipEntity, prepareMembershipPayload} from "../workspace-membership/workspace-membership-identity";
import {buildMyProjectsScopeFilter, isProjectInMyProjectsScope} from "./my-projects-scope";
import {useWorkspaceRole} from "../../system/use-workspace-role";

const membershipPopulate = {populate: {member: {populate: ["profileImage"]}}};

function withProjectMembershipPopulates(query) {
    const populate = query?.populate;
    if (!populate || typeof populate !== "object") return query;
    populate.owner = membershipPopulate;
    populate.creatorUser = membershipPopulate;
    populate.archivedBy = membershipPopulate;
    if (populate.deliverables?.populate) {
        populate.deliverables.populate.owner = membershipPopulate;
        populate.deliverables.populate.assignee = membershipPopulate;
        populate.deliverables.populate.archivedBy = membershipPopulate;
    }
    if (populate.portfolios?.populate && !Array.isArray(populate.portfolios.populate)) {
        populate.portfolios.populate.owner = membershipPopulate;
    }
    return query;
}

export function useProject() {
    const {workspaceMembershipData} = useSelector(state => state.app);
    const {userRoles} = useWorkspaceRole();
    const memberships = workspaceMembershipData?.workspace?.workspace_memberships || [];
    const normalizeProject = (item) => {
        const project = normalizeMembershipEntity(item, "project", memberships);
        if (!project) return project;
        return {
            ...project,
            deliverables: (project.deliverables || []).map((value) => normalizeMembershipEntity(value, "deliverable", memberships)),
            tasks: (project.tasks || []).map((value) => normalizeMembershipEntity(value, "task", memberships)),
            goals: (project.goals || []).map((value) => normalizeMembershipEntity(value, "goal", memberships)),
            portfolios: (project.portfolios || []).map((value) => normalizeMembershipEntity(value, "portfolio", memberships)),
            team: normalizeMembershipEntity(project.team, "team", memberships),
        };
    };

    const getProjects = async (workspaceId, strategyMap=false, options = {})=>{
        if (!workspaceId || !workspaceMembershipData?.documentId) return [];
        try {
            const project = new Project();
            const scopeFilter = buildMyProjectsScopeFilter(
                workspaceMembershipData?.workspace,
                workspaceMembershipData,
                userRoles,
            );
            let query = {
                filters: strategyMap ? {
                    $and: [
                        {workspace:{$eq: workspaceId}},
                        {archivedAt:{$null:true}},
                    ],
                } : scopeFilter,
                populate: {
                    "workspace": {fields: ["id", "documentId"]},
                    "deliverables": {
                        filters: {archivedAt: {$null: true}},
                        populate: {
                            "owner": membershipPopulate,
                            "assignee": membershipPopulate,
                            "project":true,
                            "attachments":true,
                            // each milestone's tasks: the map shows the share of them that is done (docs/tasks/19-map-milestone-progress.md)
                            "tasks": {fields: ["id", "taskStatus"], filters: {archivedAt: {$null: true}}}
                        }},
                    "goals":true,
                    "members": {populate: {user_roles:true,member:{populate:["profileImage"]}}},
                    "owner": membershipPopulate,
                    "team": {populate: {members: membershipPopulate, owner: membershipPopulate}},
                    "tasks": {filters:{$and:[{archivedAt:{$null:true}}]}},
                    "parentProjects":true,
                    "childProjects":true,
                    "portfolios":{populate: {owner: membershipPopulate}},
                    "attachments": {populate:"*"}
                }
            }

            // console.log(query)

            const _data = await project.getAll(withProjectMembershipPopulates(query));

            const normalizedProjects = (_data.data || []).map(normalizeProject);
            return strategyMap
                ? normalizedProjects
                : normalizedProjects.filter((item) => isProjectInMyProjectsScope(
                    item,
                    workspaceMembershipData?.workspace,
                    workspaceMembershipData,
                    userRoles,
                ));
        }catch (e){
            if (options.throwOnError) throw e;
            return [];
        }
    }

    const getProject = async (documentId)=>{
        const workspace = workspaceMembershipData?.workspace;
        const scopeFilter = buildMyProjectsScopeFilter(
            workspace,
            workspaceMembershipData,
            userRoles,
        );
        if (!documentId || !scopeFilter) return;
        try {
            const project = new Project();
            const query = {
                filters: scopeFilter,
                populate: {
                    "workspace": {fields: ["id", "documentId"]},
                    "deliverables": {
                        filters: {archivedAt: {$null: true}},
                        populate: {
                            "owner": membershipPopulate,
                            "assignee": membershipPopulate,
                            "project":true,
                            "attachments":true,
                            // each milestone's tasks: the map shows the share of them that is done (docs/tasks/19-map-milestone-progress.md)
                            "tasks": {fields: ["id", "taskStatus"], filters: {archivedAt: {$null: true}}}
                        }},
                    "goals":true,
                    "members": {populate: {user_roles:true, member:{populate:["profileImage"]}}},
                    "owner": membershipPopulate,
                    "tasks": {filters:{$and:[{archivedAt:{$null:true}}]}},
                    "parentProjects":true,
                    "childProjects":true,
                    "portfolios":{populate: {owner: membershipPopulate}},
                    "attachments": {populate:"*"}
                }
            }
            const _data = await project.getById(documentId, withProjectMembershipPopulates(query));

            const normalizedProject = normalizeProject(_data.data);
            return isProjectInMyProjectsScope(
                normalizedProject,
                workspace,
                workspaceMembershipData,
                userRoles,
            )
                ? normalizedProject
                : undefined;
        }catch (e){

        }
    }

    const findProjects = async (query = {}, options = {})=>{
        const workspace = workspaceMembershipData?.workspace;
        const scopeFilter = buildMyProjectsScopeFilter(
            workspace,
            workspaceMembershipData,
            userRoles,
        );
        if (!scopeFilter) return [];
        try {
            const project = new Project();
            const scopedQuery = {
                ...query,
                filters: {
                    $and: [
                        ...(query.filters ? [query.filters] : []),
                        ...scopeFilter.$and,
                    ],
                },
                populate: {
                    ...(query.populate || {}),
                    workspace: {fields: ["id", "documentId"]},
                    members: query.populate?.members || {populate: {user_roles: true, member: {populate: ["profileImage"]}}},
                    owner: membershipPopulate,
                },
            };
            const _data = await project.getAll(withProjectMembershipPopulates(scopedQuery));
            return (_data.data || [])
                .map(normalizeProject)
                .filter((item) => isProjectInMyProjectsScope(
                    item,
                    workspace,
                    workspaceMembershipData,
                    userRoles,
                ));
        }catch (e){
            if (options.throwOnError) throw e;
            return [];
        }
    }

    const updateProject = async (projectId, projectData, options = {})=>{
        // if (!permission.can("project.update", true))return;
        try {
            const project = new Project();
            const query = {populate:{
                    "workspace": {fields: ["id", "documentId"]},
                    "deliverables": {
                        filters: {archivedAt: {$null: true}},
                        populate: {
                            "owner": membershipPopulate,
                            "assignee": membershipPopulate,
                            "project":true,
                            "attachments":true,
                            // each milestone's tasks: the map shows the share of them that is done (docs/tasks/19-map-milestone-progress.md)
                            "tasks": {fields: ["id", "taskStatus"], filters: {archivedAt: {$null: true}}}
                        }},
                    "goals":true,
                    "parentProjects":true,
                    "childProjects":true,
                    "members": {populate: {user_roles:true, member:{populate:["profileImage"]}}},
                    "owner": membershipPopulate,
                    "tasks": {filters:{$and:[{archivedAt:{$null:true}}]}},
                    "portfolios":{populate: {owner: membershipPopulate}},
                    "attachments": {populate:"*"}
                }
            }
            const payload = prepareMembershipPayload("project", projectData, memberships, {
                operation: "update",
                currentMembership: workspaceMembershipData,
            });
            const _data = await project.update(projectId, payload, withProjectMembershipPopulates(query));
            return normalizeProject(_data.data)
        }catch (e){
            if (options.throwOnError) throw e;
        }
    }
    const createProject = async (projectData, options = {})=>{
        // if (!permission.can("project.create", true))return;
        try {
            const project = new Project();
            const query = {populate:{
                    "workspace": {fields: ["id", "documentId"]},
                    "deliverables": {
                        filters: {archivedAt: {$null: true}},
                        populate: {
                            "owner": membershipPopulate,
                            "assignee": membershipPopulate,
                            "project":true,
                            "attachments":true,
                            // each milestone's tasks: the map shows the share of them that is done (docs/tasks/19-map-milestone-progress.md)
                            "tasks": {fields: ["id", "taskStatus"], filters: {archivedAt: {$null: true}}}
                        }},
                    "goals":true,
                    "parentProjects":true,
                    "childProjects":true,
                    "members": {populate: {user_roles:true, member:{populate:["profileImage"]}}},
                    "owner": membershipPopulate,
                    "tasks":true,
                    "portfolios":{populate: {owner: membershipPopulate}},
                    "attachments": {populate:"*"}
                }
            }
            const payload = prepareMembershipPayload("project", projectData, memberships, {
                operation: "create",
                currentMembership: workspaceMembershipData,
            });
            const _data = await project.create(payload, withProjectMembershipPopulates(query));
            return normalizeProject(_data.data)
        }catch (e){
            if (options.throwOnError) throw e;
        }
    }

    const getProjectsProgress = async (filters)=>{
        const scopeFilter = buildMyProjectsScopeFilter(
            workspaceMembershipData?.workspace,
            workspaceMembershipData,
            userRoles,
        );
        if (!scopeFilter) return [];
        try {
            const project = new Project();
            const query = {
                filters: {
                    $and: [
                        filters,
                        ...scopeFilter.$and,
                    ],
                },
            }

            const qs = require('qs');
            const _query = qs.stringify({...query}, {
                encodeValuesOnly: true, // prettify URL
            });

            const _data = await project.action(_query, "/projects/stats");
            return _data.data
        }catch (e){

        }
    }
    const prepareTable = ({actions, config})=>{
        const fields=["title", "projectStatus", "progress", "members", "team/updated-at"]//Object.keys(DeviceEntity.fields);
        const value=(item)=>{
            return(
                <div className="flex gap-1 align-items-center text-xs">
                    <span className="pi pi-eye cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{}}/>
                    {/*<span className="pi pi-file-edit cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{actions.onEditClicked({value:item})}}/>*/}
                    {/*<span className={`pi pi-power-off cursor-pointer hover:surface-100 border-circle p-3 ${item.kphwms_isonline?"text-red-500":"text-green-500"}`} onClick={()=>{actions.onPowerOffClicked({value:item})}}/>*/}
                    {/*<span className="pi pi-sync cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{console.log(item)}}/>*/}
                    {/*<span className="pi pi-trash cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{actions.onDeleteClicked({value:item})}}/>*/}
                </div>
            )
        }
        const members=(item)=>{
            return(
                <div className="flex gap-1 align-items-center text-xs">
                    <AvatarGroup>
                        {item.members?.map(m=>(<Avatar memberData={m.member} />))}
                    </AvatarGroup>
                </div>
            )
        }
        const fieldsConfig = {
            title:{alias:"project-name", type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            projectStatus:{alias:"status", type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            progress:{type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            members:{ type:"ACTIONS", value:members, "class-name":"flex-1 pl-3 pr-3", translate:true,},
            "team/updated-at":{ type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            actions:{type:"ACTIONS", value, translate:true, "class-name":"w-3rem"},
        }

        return {fields, fieldsConfig}
    }

    const statusClassName = (status, border, bg)=>{
        switch (status) {
            case "on-way": return `${bg&&"bg-green-100"} ${border&&"border-1"} text-green-500 border-round border-green-500 p-1 px-2 w-fit text-2xs`
            case "has-risks": return `${bg&&"bg-yellow-100"} ${border&&"border-1"} text-yellow-500 border-round border-yellow-500 p-1 px-2 w-fit text-2xs`
            case "out-of-way": return `${bg&&"bg-red-100"} ${border&&"border-1"} text-red-500 border-round border-red-500 p-1 px-2 w-fit text-2xs`
            case "achieved": return `${bg&&"bg-green-100"} ${border&&"border-1"} text-green-500 border-round border-green-500 p-1 px-2 w-fit text-2xs`
            case "canceled": return `${bg&&"surface-200"} ${border&&"border-1"} text-400 border-round border-400 p-1 px-2 w-fit text-2xs`
            case "archived": return `${bg&&"bg-red-100"} ${border&&"border-1"} text-red-500 border-round border-red-500 p-1 px-2 w-fit text-2xs`
            default: return `${bg&&"surface-200"} ${border&&"border-1"} text-400 border-round border-400 p-1 px-2 w-fit text-2xs`
        }
    }

    const getArchivedProject = async (documentId) => {
        const projectModel = new Project();

        const res = await projectModel.getById(documentId, withProjectMembershipPopulates({
            populate: {
                deliverables: {
                    populate: {
                        owner: membershipPopulate,
                        assignee: membershipPopulate,
                        project: true,
                        attachments: true
                    }
                },
                goals: true,
                members: {
                    populate: {
                        user_roles: true,
                        member: {
                            populate: ["profileImage"]
                        }
                    }
                },
                owner: membershipPopulate,
                tasks: true,
                parentProjects: true,
                childProjects: true,
                portfolios: {populate: {owner: membershipPopulate}},
                attachments: {
                    populate: "*"
                }
            }
        }));

        return normalizeProject(res?.data);
    };

    return {getProjects,findProjects, createProject, updateProject, getProjectsProgress, prepareTable, statusClassName, getProject, getArchivedProject};
}
